/**
 * RingModModule — classic sideband-producing ring modulator.
 *
 * Graph:
 *
 *   input ─┬─ dryGain ───────────────────────── outputMix ─►
 *          └─► ringGain ─► wetGain ──────────── outputMix
 *   sineOsc ─────────────► ringGain.gain   (bipolar AudioParam drive)
 *
 * `ringGain.gain.value` is held at 0, so its output is purely
 * `input × sineOsc(t)` — the audio amplitude-modulated by a bipolar
 * sine, which is the definition of ring modulation. The audible
 * result is sum-and-difference sidebands, especially harsh and
 * metallic at high Freq values.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class RingModModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'ringmod' as const;
  readonly displayName = 'RING';
  readonly id: string;
  bypass = false;

  readonly freq = new NumericParameter({
    name: 'Freq',
    default: 200,
    min: 20,
    max: 5000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    // Bias low — 2kHz ring mod on every random patch would be harsh.
    randomSkew: 0.4,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.3,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    randomSkew: 0.6,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.freq, this.mix];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private ringGain: GainNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private sineOsc: OscillatorNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('ringmod');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    // The key node: its gain AudioParam is driven by the sine, with
    // base value 0, so the gain swings between -1 and +1 — real ring
    // mod, not amplitude mod.
    this.ringGain = ctx.createGain();
    this.ringGain.gain.value = 0;

    this.inputNode.connect(this.dryGain).connect(this.outputMixNode);
    this.inputNode.connect(this.ringGain).connect(this.wetGain).connect(this.outputMixNode);

    this.sineOsc = ctx.createOscillator();
    this.sineOsc.type = 'sine';
    this.sineOsc.frequency.value = this.freq.value;
    this.sineOsc.connect(this.ringGain.gain);
    this.sineOsc.start();

    this.freq.bindAudioParam(this.sineOsc.frequency, ctx);

    this.applyMix(this.mix.value, true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    try {
      this.sineOsc?.stop();
    } catch {
      /* already stopped */
    }
    this.started = false;
  }

  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Freq') return this.sineOsc?.frequency ?? null;
    return null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, immediate = false): void {
    if (!this.dryGain || !this.wetGain || !this.engine) return;
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const dry = 1 - mix;
    const wet = mix;
    if (immediate) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.01);
      this.wetGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.01);
    }
  }
}
